import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { api, apiError, useLoginMutation } from '@/store/api'
import { Button } from '@/components/ui/button'
import { Input, Label } from '@/components/ui/input'

/** Centred card on bg-muted (design.md §5.1). No self-serve reset, no registration. */
export function Login() {
  const [error, setError] = useState<string | null>(null)
  const [login, { isLoading }] = useLoginMutation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Values are read from the form on submit, not from React state: a password manager's
  // autofill sets the fields without firing input events, so state would still be empty
  // and the first submit would send blank credentials.
  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    const form = new FormData(e.currentTarget)
    try {
      await login({ email: String(form.get('email') ?? ''), password: String(form.get('password') ?? '') }).unwrap()
      // Drop the signed-out answer cached before login, or the first render after it
      // still sees "not authenticated" and sends the user back here.
      dispatch(api.util.resetApiState())
      navigate('/', { replace: true })
    } catch (err) {
      setError(apiError(err))
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted px-4">
      <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <div className="flex size-9 items-center justify-center rounded-lg bg-primary text-lg font-bold text-primary-foreground">A</div>
          <h1 className="text-lg font-semibold">Partner Dashboard</h1>
        </div>
        {error && (
          <div role="alert" className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" name="email" type="email" autoComplete="email" required />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" name="password" type="password" autoComplete="current-password" required />
          </div>
          <Button type="submit" className="w-full" loading={isLoading}>
            Log in
          </Button>
        </form>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Lost your password? Contact your Atarim account manager.
        </p>
      </div>
    </div>
  )
}
